// Shows each new ledger entry once: a line over the board for a few seconds
// and, for your own ops, the sound that was registered for it.
//
// The first snapshot only sets the mark — a save loaded mid-game must not
// replay its whole history as news.
import type { GameSnapshot } from '@aod/protocol';
import { useEffect, useRef, useState } from 'react';
import { getSoundManager } from '../audio/useSounds.js';
import { type OpLogEntry, freshOps, opSound, readOpLog } from './opNews.js';

const NOTICE_MS = 9000;

export function useOpNews(snapshot: GameSnapshot | null, seat: number): readonly OpLogEntry[] {
  const mark = useRef<number | null>(null);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const [shown, setShown] = useState<readonly OpLogEntry[]>([]);

  useEffect(() => {
    const live = timers.current;
    return () => {
      for (const t of live) clearTimeout(t);
      live.clear();
    };
  }, []);

  useEffect(() => {
    const log = readOpLog(snapshot, seat);
    const newest = log.length > 0 ? (log[log.length - 1]?.id ?? -1) : -1;
    if (mark.current === null) {
      mark.current = newest;
      return;
    }
    const fresh = freshOps(log, mark.current);
    if (fresh.length === 0) return;
    mark.current = newest;
    setShown((all) => [...all, ...fresh]);
    for (const e of fresh) {
      const sound = opSound(e);
      if (sound) getSoundManager().play(sound);
    }
    const ids = new Set(fresh.map((e) => e.id));
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setShown((all) => all.filter((e) => !ids.has(e.id)));
    }, NOTICE_MS);
    timers.current.add(timer);
  }, [snapshot, seat]);

  return shown;
}
